import ReactQuill from "react-quill";
import type { DeltaStatic } from "quill";
import { useRef } from "react";

/**
 * A source document rendered in the preview panel.
 *
 * Structurally identical to the `RagSource` shape declared locally in
 * ChatPage and ChatMessage. Declared here so the agent chat surface can
 * reuse the panel without importing from a page module.
 */
export interface PreviewDocument {
  id: string;
  title?: string;
  score?: number;
  content?: string;
  formatted_content?: DeltaStatic;
}

export interface DocumentPreviewPanelProps {
  /** The document to render, or null to render nothing. */
  document: PreviewDocument | null;
  /** Close the panel. */
  onClose: () => void;
  /** Retry loading after an error; receives the same document. */
  onRetry: (document: PreviewDocument) => void;
}

/**
 * Read-only preview of a source document.
 *
 * Extracted verbatim from ChatPage so both chat surfaces share one renderer.
 * The markup, class names and copy are unchanged from the original inline
 * block; only the data arrives through props instead of closure state. The
 * keyboard shortcuts advertised in the corner hint are still owned by the
 * page, because navigation depends on the surrounding message list.
 */
export function DocumentPreviewPanel({
  document,
  onClose,
  onRetry,
}: DocumentPreviewPanelProps) {
  const quillRef = useRef<ReactQuill>(null);

  if (!document) return null;

  return (
    <div
      className="mt-4 w-full rounded-xl bg-brand-bg p-6 shadow-card animate-slideDown origin-top relative"
      style={{ minHeight: "400px" }}
    >
      <div className="flex justify-between items-start mb-4">
        <h2 className="text-l font-bold text-black max-w-[85%] overflow-hidden">
          {document.title || "Untitled Document"}
        </h2>
        <button
          className="text-black/70 hover:text-black/90 w-8 h-8 flex items-center justify-center transition-colors flex-shrink-0"
          onClick={onClose}
          aria-label="Close document preview"
        >
          ✕
        </button>
      </div>

      <div className="absolute top-3 right-12 text-[10px] text-black/50 px-2 py-1 rounded-md bg-brand-surface">
        <div className="flex flex-col">
          <div className="flex items-center mb-1">
            <span>← →: Navigate documents</span>
          </div>
          <div className="flex items-center">
            <span>ESC: Close preview</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-black/70 mb-4 border-b border-black/10 pb-4">
        <div>Document ID: {document.id}</div>
        {document.score !== undefined && (
          <div className="font-semibold bg-brand-surface rounded-lg px-3 py-1 text-black/90 flex items-center">
            <span className="mr-1">Relevance:</span>
            <span className="font-bold text-black">
              {(document.score * 100).toFixed(1)}%
            </span>
            <div className="ml-2 h-2 w-16 bg-brand-border rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-primary"
                style={{
                  width: `${Math.min(
                    100,
                    Math.round(document.score * 100)
                  )}%`,
                  opacity: Math.max(0.5, document.score),
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      <div className="prose max-w-none whitespace-pre-wrap text-left text-black overflow-auto">
        {document.content === "Loading content..." ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-2"></div>
            <p className="text-black/70">Loading document content...</p>
          </div>
        ) : document.content === "Error loading content. Please try again." ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-brand-error/10 p-4 rounded-lg text-brand-error mb-4">
              <p className="font-bold">Error loading content</p>
              <p className="text-sm">
                There was a problem retrieving the document content.
              </p>
              <button
                className="mt-2 px-3 py-1 bg-brand-error text-white rounded-md text-sm hover:opacity-90"
                onClick={() => onRetry(document)}
              >
                Try Again
              </button>
            </div>
          </div>
        ) : document.formatted_content ? (
          <ReactQuill
            ref={quillRef}
            value={document.formatted_content}
            readOnly
            theme="bubble"
            modules={{ toolbar: false }}
          />
        ) : (
          document.content || "No content available"
        )}
      </div>
    </div>
  );
}
